/* US PREPPERS — UX enhancements (ux.js)
   Loaded with defer in default.hbs. All logic is defensive: every
   block guards on element presence, so it is a no-op on pages that
   do not use a given feature. */
(function () {
    'use strict';

    // Signal that JS is active — CSS uses html.js to swap the scrollbar
    // affordance for arrows/dots (see ux.css §11).
    document.documentElement.classList.add('js');

    // Guard against double-execution (some environments re-run defer scripts).
    if (window.__uspUxLoaded) {
        return;
    }
    window.__uspUxLoaded = true;

    /* Programmatic scrolling is motion too. Read the media query live (not
       once at load) so a mid-session change of the OS setting is honored.
       `auto` keeps the destination identical and jumps instantly. */
    function scrollBehavior() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth';
    }

    /* ------------------------------------------------------------
       2. READING PROGRESS BAR
       ------------------------------------------------------------ */
    var progressBar = document.getElementById('ux-progress-bar');
    var progressFill = progressBar ? progressBar.querySelector('.ux-progress-bar-fill') : null;
    if (progressBar && progressFill) {
        var ticking = false;

        function updateProgress() {
            var doc = document.documentElement;
            var scrollable = doc.scrollHeight - window.innerHeight;
            var pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
            progressFill.style.width = Math.min(100, Math.max(0, pct)) + '%';
            ticking = false;
        }

        window.addEventListener('scroll', function () {
            if (!ticking) {
                window.requestAnimationFrame(updateProgress);
                ticking = true;
            }
        }, { passive: true });
        window.addEventListener('resize', updateProgress);
        updateProgress();
    }

    /* ------------------------------------------------------------
       3. TABLE OF CONTENTS
       Level-aware, built from H2 *and* H3. Keying to H2 only meant posts
       split by H3 (1,300–2,200 words with 1 H2 + 8 H3) got no orientation
       at all, while the longest post got ~50 chips (a full screen).
         - gate: 3+ headings in total
         - level choice: H2-only once a post has more than 8 H2s (H3s would
           double the list without adding top-level structure)
         - cap: at most 12 chips, so density cannot invert
         - cap order: LEVEL-RESPECTING — every H2 is taken before any H3, so
           the cap can never hide top-level structure behind sub-headings.
           If the H2s alone exceed the cap, the list is H2s only and the tail
           is truncated (announced by the `+N more sections` item).
         - overflow: one non-link item states how many headings were dropped,
           so the strip never claims to be the whole outline.
       Rendered as a compact always-visible strip of links (no collapse).
       ------------------------------------------------------------ */
    var toc = document.getElementById('ux-toc');
    if (toc) {
        var TOC_MAX_ITEMS = 12;
        var TOC_H2_ONLY_ABOVE = 8;
        var tocH2s = Array.prototype.slice.call(document.querySelectorAll('.gh-content h2'));
        var tocH3s = Array.prototype.slice.call(document.querySelectorAll('.gh-content h3'));
        var tocList = toc.querySelector('.ux-toc-list');

        // H2s first, then H3s fill whatever slots the cap leaves.
        var h2Only = tocH2s.length > TOC_H2_ONLY_ABOVE;
        var selected = tocH2s.slice(0, TOC_MAX_ITEMS);
        if (!h2Only) {
            selected = selected.concat(tocH3s.slice(0, TOC_MAX_ITEMS - selected.length));
        }
        // Membership is decided by level (H2 before H3); ORDER comes from the
        // document. Without this merge the anchors stopped being monotonic
        // with the page — on /seed-saving-guide/ a subsection from the middle
        // of the article listed after the last section.
        var headings = selected.slice().sort(function (a, b) {
            if (a === b) {
                return 0;
            }
            return a.compareDocumentPosition(b) & 4 ? -1 : 1;  // 4 = FOLLOWING
        });
        // Headings the strip actually stands for, and the ones it omits.
        // The count is against the FULL heading population, so a heading
        // filtered out by the H2-only rule is disclosed too — the strip must
        // never claim to be the whole outline.
        var tocTotal = tocH2s.length + tocH3s.length;
        var tocDropped = tocTotal - headings.length;

        if (headings.length >= 3 && tocList) {
            // Idempotent: clear any pre-existing items so a double-run (or
            // bfcache restore) can never produce duplicate entries.
            tocList.innerHTML = '';
            var tocCounters = { h2: 0, h3: 0 };
            Array.prototype.forEach.call(headings, function (heading) {
                var level = heading.tagName.toLowerCase();
                tocCounters[level] += 1;
                var id = heading.id || ('ux-' + level + '-' + tocCounters[level]);
                if (!heading.id) {
                    heading.id = id;
                }
                var li = document.createElement('li');
                var a = document.createElement('a');
                a.href = '#' + id;
                a.textContent = heading.textContent.trim();
                if (level === 'h3') {
                    a.className = 'ux-toc-sub';
                }
                li.appendChild(a);
                tocList.appendChild(li);
            });

            // One non-link item naming the size of the omission. It carries
            // real information (the strip is not the whole outline), so it is
            // NOT aria-hidden; it is plain text rather than a link because
            // there is nothing to navigate to — the remaining headings live
            // in the page itself.
            if (tocDropped > 0) {
                var more = document.createElement('li');
                more.className = 'ux-toc-more';
                more.textContent = '+' + tocDropped + ' more section' +
                    (tocDropped === 1 ? '' : 's');
                tocList.appendChild(more);
            }

            // Reveal the always-visible compact TOC strip.
            toc.hidden = false;
        } else {
            // Not enough headings — remove the container entirely.
            toc.parentNode.removeChild(toc);
        }
    }

    /* ------------------------------------------------------------
       5. COMMENTS EMPTY-BLOCK GUARD
       The comments-ui widget injects its UI into an iframe. If it
       fails to bootstrap (blank srcdoc), the iframe still reserves
       space. Detect the blank iframe and collapse the container.
       ------------------------------------------------------------ */
    var comments = document.querySelector('.gh-comments');
    if (comments) {
        function checkBlankComments() {
            var iframe = comments.querySelector('iframe');
            if (!iframe) {
                return;
            }
            var blank = false;
            try {
                // srcdoc iframes are same-origin; a bootstrap-less one
                // has an empty body (just the injected doctype).
                var body = iframe.contentDocument && iframe.contentDocument.body;
                blank = !body || (body.innerHTML || '').trim().length === 0;
            } catch (e) {
                // Cross-origin iframe => widget likely loaded real content.
                blank = false;
            }
            if (blank) {
                comments.setAttribute('data-empty', 'true');
            }
        }
        // comments-ui initialises async; re-check at a few intervals.
        [1500, 3000, 5000].forEach(function (ms) {
            window.setTimeout(checkBlankComments, ms);
        });
    }

    /* ------------------------------------------------------------
       7. NEWSLETTER VALIDATION FEEDBACK
       Guards the members form against empty/invalid submissions even
       when Portal JS is unavailable, and surfaces a visible message.
       Capture-phase so it runs before any Portal submit handler.
       ------------------------------------------------------------ */
    var memberForms = document.querySelectorAll('form[data-members-form]');
    Array.prototype.forEach.call(memberForms, function (form) {
        var email = form.querySelector('input[data-members-email], input[type="email"]');
        var error = form.querySelector('[data-members-error]');
        if (!email) {
            return;
        }

        form.addEventListener('submit', function (e) {
            var value = (email.value || '').trim();
            if (!value || !email.checkValidity()) {
                e.preventDefault();
                e.stopImmediatePropagation();
                if (error) {
                    error.textContent = 'Please enter a valid email address.';
                }
                email.setAttribute('aria-invalid', 'true');
                email.focus();
            } else {
                // Valid submission — clear any lingering invalid state so
                // aria-invalid doesn't stick after a successful submit.
                email.removeAttribute('aria-invalid');
                if (error) {
                    error.textContent = '';
                }
            }
        }, true);

        email.addEventListener('input', function () {
            if (email.checkValidity()) {
                if (error) {
                    error.textContent = '';
                }
                email.removeAttribute('aria-invalid');
            }
        });
    });

    /* ------------------------------------------------------------
       6. CONTACT FORM (mailto)
       ------------------------------------------------------------ */
    var contactForm = document.getElementById('ux-contact-form');
    if (contactForm) {
        var CONTACT_EMAIL = 'contact@uspreppers.com';
        var feedback = contactForm.querySelector('.ux-form-feedback');

        /* Message state machine for the aria-live feedback element.
           'error'   — an attempt failed; the message names the *first*
                       invalid field and is refreshed, not cleared, on input.
           'idle'    — no message on screen (nothing attempted yet, or the
                       form is currently valid).
           'done'    — an attempt was handed to the mail app; the copy is the
                       truthful mailto disclaimer. Editing the form ends that
                       report (the form no longer matches what was handed
                       over), so the next input returns to 'idle'.
           `attempted` is separate from `messageState` on purpose: the watcher
           must stay armed for the rest of the page's life once a submit has
           happened. Using 'idle' (valid-after-attempt) as the "stay quiet"
           test disarmed it, so re-breaking a form that had been valid wrote
           nothing at all. */
        var messageState = 'idle';
        var attempted = false;

        function firstInvalidField() {
            return contactForm.querySelector('input:invalid, textarea:invalid');
        }

        function fieldLabel(field) {
            var label = field.id
                ? contactForm.querySelector('label[for="' + field.id + '"]')
                : null;
            return label
                ? label.textContent.replace(/\s*\(required\)\s*$/, '').replace(/\s*\*\s*$/, '').trim()
                : (field.getAttribute('name') || 'This field');
        }

        function showValidationError(field) {
            if (!feedback) {
                return;
            }
            feedback.classList.remove('is-success');
            setMessage(fieldLabel(field) + ': ' +
                (field.validationMessage || 'please check this field.'));
        }

        /* Single writer for the live region. A keystroke that cannot change
           what the user needs to hear — same first-invalid field, same text —
           must not touch the DOM: rewriting an identical string is a mutation
           that a screen reader may re-announce on every key. */
        function setMessage(text) {
            if (feedback && feedback.textContent !== text) {
                feedback.textContent = text;
            }
        }

        /* Single re-check used by both submit and input, so the two paths
           cannot disagree about what the form's state is. */
        function refreshValidationMessage() {
            var invalid = firstInvalidField();
            if (invalid) {
                messageState = 'error';
                showValidationError(invalid);
                return;
            }
            messageState = 'idle';
            if (feedback) {
                feedback.classList.remove('is-success');
                setMessage('');
            }
        }

        contactForm.addEventListener('submit', function (e) {
            e.preventDefault();
            attempted = true;

            if (!contactForm.checkValidity()) {
                // Continuous feedback: the UA bubble (reportValidity) is
                // end-of-interaction only and vanishes on the next click.
                // Mirror the first invalid field's own message into the
                // aria-live element as well, so the error is announced and
                // stays on screen until the form is actually valid.
                refreshValidationMessage();
                contactForm.reportValidity();
                return;
            }

            var name = (contactForm.querySelector('[name="name"]') || {}).value || '';
            var email = (contactForm.querySelector('[name="email"]') || {}).value || '';
            var subject = (contactForm.querySelector('[name="subject"]') || {}).value || '';
            var message = (contactForm.querySelector('[name="message"]') || {}).value || '';

            var body = 'Name: ' + name + '\nEmail: ' + email + '\n\n' + message;
            var mailto = 'mailto:' + CONTACT_EMAIL +
                '?subject=' + encodeURIComponent(subject || ('Message from ' + name)) +
                '&body=' + encodeURIComponent(body);

            window.location.href = mailto;

            if (feedback) {
                // Truthful copy: this is a mailto: handoff, not a delivery.
                // Nothing has been sent from this page and we cannot observe
                // whether an app opened, so say exactly that.
                messageState = 'done';
                feedback.classList.add('is-success');
                setMessage('Not sent yet — your mail app should have opened with the message ready to send. If nothing opened, write to ' + CONTACT_EMAIL + ' directly.');
            }
        });

        /* Once an attempt has happened, every input for the rest of the page's
           life re-runs the same validity check and refreshes the message:
           error text for the first invalid field (which may be a different
           field than the one being typed in), empty only when the whole form
           is valid. Before the first attempt the form stays quiet. */
        contactForm.addEventListener('input', function () {
            if (!attempted) {
                return;
            }
            refreshValidationMessage();
        });
    }

    /* ------------------------------------------------------------
       8. BURGER ARIA STATE SYNC
       Keep aria-expanded on the burger in sync with the open menu.
       ------------------------------------------------------------ */
    var burger = document.querySelector('.gh-burger');
    var nav = document.getElementById('gh-navigation');
    if (burger && nav) {
        burger.addEventListener('click', function () {
            window.setTimeout(function () {
                var open = nav.classList.contains('is-open');
                burger.setAttribute('aria-expanded', open ? 'true' : 'false');
            }, 0);
        });
    }

    /* ------------------------------------------------------------
       8b. "MORE" DROPDOWN TOGGLE ARIA STATE SYNC
       The overflow "More" button is created dynamically by dropdown.js
       (bundled source.js), so it isn't present when this defer script
       runs. Seed aria-expanded="false" when the button appears, then
       reflect the menu's open/closed state (.is-dropdown-open on the
       nav) on every click — toggle opens AND outside-clicks close it.
       ------------------------------------------------------------ */
    var moreNav = document.getElementById('gh-navigation');
    if (moreNav) {
        function syncMoreAria() {
            var toggle = document.querySelector('.gh-more-toggle');
            if (!toggle) {
                return;
            }
            var open = moreNav.classList.contains('is-dropdown-open');
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }

        // Seed any toggle already present (fast loads / bfcache restore).
        syncMoreAria();

        // Seed future toggles — dropdown.js re-creates the button on resize.
        if (window.MutationObserver) {
            var moreObserver = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
                        if (node.nodeType === 1 && node.classList &&
                            node.classList.contains('gh-more-toggle')) {
                            syncMoreAria();
                        }
                    });
                });
            });
            moreObserver.observe(moreNav, { childList: true, subtree: true });
        }

        // The dropdown opens/closes on any click (toggle or outside-close),
        // so re-sync aria-expanded after each click's state change.
        document.addEventListener('click', function () {
            window.setTimeout(syncMoreAria, 0);
        });
    }

    /* ------------------------------------------------------------
       9. COLLECTION BREADCRUMB + TAG PILL
       Internal #-tags are filtered out of the theme's post.tags context,
       so the collection name is read from the body's tag-<slug> class and
       injected into the breadcrumb and article tag-pill placeholders.
       ------------------------------------------------------------ */
    var COLLECTIONS = {
        'hash-sustenance':     { label: 'Food & Water',    url: '/sustenance/' },
        'hash-shelter-power':  { label: 'Shelter & Power', url: '/shelter-power/' },
        'hash-gear':           { label: 'Gear',            url: '/gear/' },
        'hash-safety':         { label: 'Safety',          url: '/safety/' },
        'hash-health':         { label: 'Health',          url: '/health/' },
        'hash-skills':         { label: 'Skills',          url: '/skills/' }
    };
    var bodyTagMatch = document.body.className.match(/tag-(sustenance|shelter-power|gear|safety|health|skills)/);
    if (bodyTagMatch) {
        var collection = COLLECTIONS['hash-' + bodyTagMatch[1]];
        if (collection) {
            var crumbEl = document.querySelector('.ux-breadcrumb-collection');
            if (crumbEl && !crumbEl.innerHTML.trim()) {
                crumbEl.innerHTML = '<a href="' + collection.url + '">' + collection.label + '</a>' +
                    '<span class="ux-breadcrumb-sep" aria-hidden="true">/</span>';
            }
            var pillEl = document.querySelector('.ux-article-collection');
            if (pillEl && !pillEl.innerHTML.trim()) {
                pillEl.innerHTML = '<a class="gh-article-tag" href="' + collection.url + '">' + collection.label + '</a>';
            }
        }
    }

    /* ------------------------------------------------------------
       10. EXTERNAL LINKS OPEN IN NEW TAB
       Ghost's Lexical->HTML renderer strips target="_blank", so we add
       it client-side. rel (noopener / nofollow noopener) is set by the
       converter and already survives into the HTML.
       ------------------------------------------------------------ */
    var host = window.location.hostname;
    var extLinks = document.querySelectorAll('.gh-content a[href^="http"]');
    Array.prototype.forEach.call(extLinks, function (a) {
        var url;
        try { url = new URL(a.getAttribute('href')); } catch (e) { return; }
        if (url.hostname === host) { return; }
        a.setAttribute('target', '_blank');
        var rel = a.getAttribute('rel') || '';
        if (rel.indexOf('noopener') === -1) {
            rel = rel ? rel + ' noopener' : 'noopener';
            a.setAttribute('rel', rel);
        }
    });

    /* ------------------------------------------------------------
       11. SHARE BUTTON (Web Share API + copy-link fallback)
       The article Share link (href="#/share") has no server-side
       handler. Clicking it opens the native share sheet via
       navigator.share() where supported; otherwise it copies the
       article URL to the clipboard and confirms with a transient
       button-label change.
       ------------------------------------------------------------ */
    var shareButtons = document.querySelectorAll('.gh-button-share');
    Array.prototype.forEach.call(shareButtons, function (btn) {
        btn.addEventListener('click', function (e) {
            e.preventDefault();

            var url = window.location.href;
            var title = (document.title || '').trim();

            function showCopiedFeedback() {
                if (!btn.dataset.originalLabel) {
                    btn.dataset.originalLabel = btn.textContent.trim();
                }
                btn.textContent = 'Link copied!';
                window.setTimeout(function () {
                    if (btn.dataset.originalLabel) {
                        btn.textContent = btn.dataset.originalLabel;
                    }
                }, 2000);
            }

            function showErrorFeedback() {
                if (!btn.dataset.originalLabel) {
                    btn.dataset.originalLabel = btn.textContent.trim();
                }
                btn.textContent = 'Copy failed';
                window.setTimeout(function () {
                    if (btn.dataset.originalLabel) {
                        btn.textContent = btn.dataset.originalLabel;
                    }
                }, 2500);
            }

            function copyFallback() {
                // Clipboard is unavailable or denied. Flag the failure on the
                // button, then surface the URL so the user can copy manually.
                showErrorFeedback();
                try {
                    window.prompt('Copy this link:', url);
                } catch (err) {
                    // prompt() blocked (e.g. sandboxed iframe) — nothing left.
                }
            }

            function legacyCopy(text) {
                var ta = document.createElement('textarea');
                ta.value = text;
                ta.setAttribute('readonly', '');
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                var ok = false;
                try {
                    ok = document.execCommand('copy');
                } catch (err) {
                    ok = false;
                }
                document.body.removeChild(ta);
                return ok;
            }

            function copyLink() {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(url).then(showCopiedFeedback, function () {
                        if (legacyCopy(url)) {
                            showCopiedFeedback();
                        } else {
                            copyFallback();
                        }
                    });
                } else if (legacyCopy(url)) {
                    showCopiedFeedback();
                } else {
                    copyFallback();
                }
            }

            if (navigator.share) {
                navigator.share({
                    title: title,
                    url: url
                }).catch(function (err) {
                    // AbortError = user dismissed the share sheet; do nothing.
                    if (err && err.name === 'AbortError') {
                        return;
                    }
                    // Any other failure (unsupported, permission, etc.)
                    // falls back to copying the link.
                    copyLink();
                });
            } else {
                copyLink();
            }
        });
    });

    /* ------------------------------------------------------------
       12. "START WITH THESE" CAROUSEL — arrows + position dots
       The .gh-start-v2-grid is a horizontal scroll-snap strip whose
       scrollbar is hidden once JS is active, so provide an explicit
       affordance: prev/next edge arrows (disabled at the ends) and a
       clickable position dot per card. The grid itself is a focusable
       scroll region (tabindex="0") so keyboard Left/Right scroll it.
       ------------------------------------------------------------ */
    var startCarousel = document.querySelector('.gh-start-v2-carousel');
    if (startCarousel) {
        var startGrid = startCarousel.querySelector('.gh-start-v2-grid');
        var startPrev = startCarousel.querySelector('.gh-start-v2-nav-prev');
        var startNext = startCarousel.querySelector('.gh-start-v2-nav-next');
        var startDotsEl = startCarousel.parentNode ? startCarousel.parentNode.querySelector('.gh-start-v2-dots') : null;
        var startCards = startGrid ? startGrid.querySelectorAll('.gh-start-card') : [];
        var startDots = [];

        // Build one clickable dot per card.
        if (startDotsEl && startCards.length) {
            Array.prototype.forEach.call(startCards, function (card, i) {
                var dot = document.createElement('button');
                dot.type = 'button';
                dot.className = 'gh-start-v2-dot';
                dot.setAttribute('tabindex', '-1');
                dot.setAttribute('aria-hidden', 'true');
                dot.addEventListener('click', function () {
                    card.scrollIntoView({ behavior: scrollBehavior(), inline: 'start', block: 'nearest' });
                });
                startDotsEl.appendChild(dot);
                startDots.push(dot);
            });
        }

        // Width of one scroll step = card width + column gap.
        function startStep() {
            if (!startCards.length || !startGrid) {
                return 300;
            }
            var gap = parseFloat(window.getComputedStyle(startGrid).columnGap || '18') || 18;
            return startCards[0].getBoundingClientRect().width + gap;
        }

        // Sync arrow disabled state and the active dot from scroll position.
        function startUpdate() {
            if (!startGrid) {
                return;
            }
            var maxScroll = startGrid.scrollWidth - startGrid.clientWidth;
            var atStart = startGrid.scrollLeft <= 2;
            var atEnd = startGrid.scrollLeft >= maxScroll - 2;
            if (startPrev) {
                startPrev.disabled = atStart;
            }
            if (startNext) {
                startNext.disabled = atEnd;
            }
            if (startDots.length) {
                // Map scroll position proportionally across the dots (0 -> first,
                // maxScroll -> last). More robust than scrollLeft/step, which drifts
                // because the last "step" is shorter than a full card.
                var active = maxScroll > 0 ? Math.round(startGrid.scrollLeft / maxScroll * (startDots.length - 1)) : 0;
                active = Math.max(0, Math.min(startDots.length - 1, active));
                startDots.forEach(function (dot, i) {
                    dot.classList.toggle('is-active', i === active);
                });
            }
        }

        if (startPrev && startGrid) {
            startPrev.addEventListener('click', function () {
                startGrid.scrollBy({ left: -startStep(), behavior: scrollBehavior() });
            });
        }
        if (startNext && startGrid) {
            startNext.addEventListener('click', function () {
                startGrid.scrollBy({ left: startStep(), behavior: scrollBehavior() });
            });
        }

        if (startGrid) {
            var startScrollTicking = false;
            startGrid.addEventListener('scroll', function () {
                if (!startScrollTicking) {
                    window.requestAnimationFrame(function () {
                        startScrollTicking = false;
                        startUpdate();
                    });
                    startScrollTicking = true;
                }
            }, { passive: true });
            window.addEventListener('resize', startUpdate);
            // The theme's async CSS (media="print" -> onload) applies after this
            // defer script runs, so card widths settle later. Re-check on window
            // load and shortly after so the arrow disabled state tracks the real
            // (post-CSS) overflow instead of a transient non-overflowing layout.
            window.addEventListener('load', startUpdate);
            window.setTimeout(startUpdate, 300);
            startUpdate();
        }
    }
})();
